import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth as authApi, API_BASE_URL, getAuthHeader } from '../utils/api';
import PcgsApiKeyModal from '../components/PcgsApiKeyModal';
import ErrorDisplay from '../components/ErrorDisplay';

// Define public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password', // Base path will match /reset-password/:token
  '/shared', // Allow access to shared set views
  '/privacy-policy',
  '/terms-of-service'
];

// Helper to check if current path is public
const isPublicPath = (pathname) => {
  if (!pathname) return false;
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route));
};

const AuthContext = createContext(null);

const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes - more frequent refresh

export const AuthProvider = ({ children, pathname }) => {
  const [user, setUser] = useState(null);
  // Initialize loading as false for public routes
  const [loading, setLoading] = useState(!isPublicPath(pathname));
  const [error, setError] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showPcgsModal, setShowPcgsModal] = useState(false);

  const navigate = useNavigate();

  // Get fresh tokens by calling /me endpoint
  const refreshTokens = async () => {
    try {
      console.debug('Refreshing auth tokens');
      const headers = await getAuthHeader('GET');
      const response = await fetch(`${API_BASE_URL}/users/auth/me`, {
        headers,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh tokens');
      }
      
      // Update user data
      const userData = await response.json();
      setUser(userData);
      
      return response;
    } catch (err) {
      console.error('Failed to refresh tokens:', err);
      // If we can't refresh tokens, force logout
      await logout();
      throw err;
    }
  };

  // Track user activity for admin dashboard
  useEffect(() => {
    if (!user) return;
    
    const updateActivity = () => {
      setLastActivity(Date.now());
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [user]);

  // Periodic token refresh and CSRF error handling
  useEffect(() => {
    if (!user) return; // Only run for authenticated users

    console.debug('Setting up token refresh interval');

    // Refresh token periodically
    const tokenRefreshInterval = setInterval(async () => {
      try {
        console.debug('Performing periodic token refresh');
        await refreshTokens();
      } catch (err) {
        console.error('Failed to refresh token in interval:', err);
      }
    }, TOKEN_REFRESH_INTERVAL);

    return () => {
      clearInterval(tokenRefreshInterval);
    };
  }, [user]);

  // Check authentication status on mount and when pathname changes
  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      // Skip auth check for public routes
      if (isPublicPath(pathname)) {
        if (isMounted) setLoading(false);
        return;
      }

      // Set loading for protected routes
      if (isMounted) setLoading(true);
      
      try {
        // Get initial user data
        const userData = await authApi.getCurrentUser();
        
        if (!isMounted) return;
        
        setUser(userData);
        setLastActivity(Date.now());
      } catch (err) {
        console.error('Authentication check failed:', err);
        if (!isMounted) return;
        
        // Clean up auth state before redirecting
        setUser(null);
        setLastActivity(0);
        setError('Authentication failed');
        
        // Save the current path before redirecting
        if (!isPublicPath(pathname)) {
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          localStorage.setItem('redirectPath', currentPath);
          navigate('/login', { 
            replace: true,
            state: { from: { pathname: currentPath } }
          });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    checkAuth();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [pathname, navigate]);

  const login = async (email, password) => {
    try {
      setError(null);
      const userData = await authApi.login(email, password);
      setUser(userData);
      setLastActivity(Date.now());
      
      // Check if user needs PCGS API key notification
      if (userData && !userData.pcgs_api_key) {
        setShowPcgsModal(true);
      }
      
      return userData;
    } catch (err) {
      console.error('Login failed:', err);
      setError('Invalid credentials');
      throw err;
    }
  };

  const register = async (email, password, firstName, lastName, inviteCode, roleId) => {
    try {
      setError(null);
      const userData = await authApi.register(
        email, password, firstName, lastName, inviteCode, roleId
      );
      setUser(userData);
      setLastActivity(Date.now());
      
      // Check if user needs PCGS API key notification
      if (!userData.pcgs_api_key) {
        setShowPcgsModal(true);
      }
      
      return userData;
    } catch (err) {
      console.error('Registration failed:', err);
      setError('Registration failed');
      throw err;
    }
  };

  const logout = async () => {
    // Clean up auth state
    setUser(null);
    setLastActivity(0);
    setError(null);
    
    // Use React Router navigation
    navigate('/login', { replace: true });
    
    // Perform logout API call in the background
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const updateUser = async () => {
    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      setLastActivity(Date.now());
      return userData;
    } catch (err) {
      console.error('Failed to update user:', err);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateUser,
    refreshTokens,
    isAuthenticated: !!user,
    lastActivity
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <PcgsApiKeyModal 
        isOpen={showPcgsModal} 
        onClose={() => setShowPcgsModal(false)}
        onGoToProfile={() => {
          setShowPcgsModal(false);
          navigate('/profile');
        }}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
